import { useEffect } from 'react';

import { useHashScroll } from '@sureapp/wl-toggle-common/dist/hooks/useHashScroll';
import { analytics } from '@sureapp/wl-toggle-common/dist/utils';
import kebabCase from 'lodash/kebabCase';
import lowerCase from 'lodash/lowerCase';
import ReactHtmlParser, { convertNodeToElement } from 'react-html-parser';
import { HashLink } from 'react-router-hash-link';

import CmsContent, { TRenderCmsContent } from '@@/auto/components/CmsContent';
import personalInformation from '@@/auto/constants/privacyPolicyPersonalInformation';
import { TConfig } from '@@/auto/interfaces';
import LayoutSelfService from '@@/common/components/LayoutSelfService';
import { useCmsContent } from '@@/common/hooks/useCmsContent';

import SpinnerView from '../SpinnerView';

import classes from './PrivacyPolicyView.module.css';

type TCreateCmsRenderer = (fields: string[]) => TRenderCmsContent;

const createCmsContentRendererForFields: TCreateCmsRenderer = (fieldNames) => ({
  // eslint-disable-next-line react/prop-types
  cmsContent,
}) => {
  const sections =
    // eslint-disable-next-line react/prop-types
    cmsContent?.fields.filter((field) =>
      fieldNames.includes(field.field_template.name)
    ) || [];

  return (
    <>
      {sections?.map((htmlSection) => {
        const fieldName = kebabCase(htmlSection.field_template.name);
        return (
          <div
            id={fieldName}
            key={htmlSection?.id}
            className={classes[fieldName]}
          >
            {ReactHtmlParser(htmlSection?.text || '', {
              transform: function transformAnchorTags(node, index) {
                if (node.type === 'tag' && node.name === 'h2') {
                  return (
                    <a
                      className={classes.sectionHeading}
                      href={`#${fieldName}`}
                    >
                      {convertNodeToElement(node, index, transformAnchorTags)}
                    </a>
                  );
                }
                return convertNodeToElement(node, index, transformAnchorTags);
              },
            })}
          </div>
        );
      })}
    </>
  );
};

export type TPrivacyPolicyView = {
  supportEmail: TConfig['supportEmail'];
  privacyPolicySlug: string;
  insuranceUrl?: string;
};

const cmsModelSlug = 'privacy-policy';

function PrivacyPolicyView({
  privacyPolicySlug,
  supportEmail,
  insuranceUrl = 'gettoggle.com',
}: TPrivacyPolicyView) {
  const cmsContent = useCmsContent(cmsModelSlug);

  useEffect(() => {
    analytics.page();
  }, []);

  useHashScroll();

  if (!cmsContent) {
    return <SpinnerView />;
  }

  return (
    <LayoutSelfService
      className={classes.root}
      header="Privacy Policy"
      backgroundImgSrc={null}
    >
      <CmsContent
        data-testid="privacyPolicy-introduction-content"
        modelSlug={cmsModelSlug}
        entrySlug={privacyPolicySlug}
        renderContent={createCmsContentRendererForFields([
          'Introduction',
          'Personal Information We Collect',
        ])}
      />

      <table className={classes.personalInfoTable}>
        <thead>
          <tr>
            <td>Category</td>
            <td>Purpose of Use</td>
            <td>What may be included in this category</td>
            <td>Some examples</td>
          </tr>
        </thead>
        <tbody>
          {personalInformation.map((item) => {
            const key = lowerCase(item.title);
            return (
              <tr key={key} data-testid={`PersonalInformationTable_${key}`}>
                <td>{item.title}</td>
                <td>{item.purposeOfUse}</td>
                <td>{item.included}</td>
                <td>{item.examples}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <section>
        <HashLink
          id="do-not-sell-my-personal-information"
          to="/privacy#do-not-sell-my-personal-information"
          className={classes.sectionHeading}
        >
          <h2>Do Not Sell My Personal Information</h2>
        </HashLink>
        <p>
          We collect certain information (&quot;nonpublic personal
          information&quot;) about you and the members of your household
          (&quot;you&quot;) from the following sources:
        </p>
        <p>
          We receive and store information about you from a variety of sources:
        </p>
        <ul className="preserve_default_browser_styles">
          <li>
            <strong>Information you provide to us:</strong> we collect the
            following categories of information directly from you: Internal,
            Historical, External, Financial, Social. Specifically, we collect
            information you provide to us, for example, in your application,
            which includes your name, date of birth, email address, physical
            address, phone number, payment method, and as applicable to pay
            claims, bank account information. We may also collect your policy or
            claim information. If you submit any such information relating to
            people other than yourself, you represent that you have the
            authority to do so and to permit us to use the information in
            accordance with this Privacy Policy;
          </li>
          <li>
            <strong>Information we collect automatically:</strong> we collect
            the following categories of information indirectly from you:
            Tracking. Specifically, we collect information about you and your
            use of this Site and our Services, such as your interactions with us
            and our online advertising, as well as information regarding your
            computer or other device used to access our Services. This
            information includes:
            <ul className="preserve_default_browser_styles">
              <li>
                your activity on {insuranceUrl}, use of our social media sites,
                and your interaction with our online advertisements;
              </li>
              <li>device IDs or other unique identifiers;</li>
              <li>
                device and software characteristics, connection information,
                statistics on page views, referring source, IP address (which
                may tell us your general location), browser and standard web
                server log information;
              </li>
              <li>
                details of your interactions with our support representatives,
                such as date, time, and reason for contacting us, transcripts of
                any chat conversations, and if you call us or we call you, your
                phone number and call recordings;
              </li>
              <li>
                details about your transactions with us, our affiliates or
                others, such as your policy coverage, premiums, and payment
                history; and
              </li>
              <li>
                details collected via the use of cookies, web beacons and other
                technologies.
              </li>
            </ul>
          </li>
          <li>
            <strong>Information obtained from other sources:</strong> we also
            obtain the following categories of information from other sources:
            Historical, External, Financial, Social. We protect this information
            according to the practices described in this Privacy Policy, plus
            any additional restrictions imposed by the source of the data. The
            sources vary over time, but the information collected includes:
            <ul className="preserve_default_browser_styles">
              <li>
                credit report information, insurance credit score, and claims
                history from a consumer reporting agency or insurance support
                organization;
              </li>
              <li>
                details of your interactions with select strategic partners with
                which we offer co-branded Services or engage in joint marketing
                activities; and
              </li>
              <li>details about you from publicly available sources.</li>
            </ul>
          </li>
        </ul>
      </section>

      <CmsContent
        data-testid="privacyPolicy-middle-content"
        modelSlug={cmsModelSlug}
        entrySlug={privacyPolicySlug}
        renderContent={createCmsContentRendererForFields([
          'How We Use Your Personal Information',
          'How We Protect Your Personal Information',
          'Personal Information We Disclose',
        ])}
      />

      <section>
        <h2>Sharing Personal Information</h2>
        <p>
          In accordance with the California Consumer Privacy Act (“CCPA”), we
          may disclose your personal information, for a business purpose, to a
          third-party service provider. When we disclose personal information to
          a service provider for a business purpose, we enter a contract that
          describes the purpose and requires the recipient to both keep that
          personal information confidential and not use it for any purpose
          except performing the contract.
        </p>
        <p>
          We share your personal information with the following categories of
          third parties:
        </p>
        <ul className="preserve_default_browser_styles">
          <li>
            Service providers which may include, but are not limited to:
            <ul className="preserve_default_browser_styles">
              <li>Internet service providers</li>
              <li>Data analytics providers</li>
            </ul>
          </li>
          <li>Government entities</li>
          <li>Consumer reporting agencies</li>
          <li>Insurance support organizations</li>
        </ul>
        <p>
          In addition to sharing your personal information with third-party
          service providers, we may share your information as permitted by
          applicable federal law as described in our customer privacy notices.
        </p>
        <p>
          In the previous twelve (12) months, we may have disclosed the
          following categories of personal information for a business purpose:
          internal, historical, financial, external, social and tracking.
        </p>
        <p>
          In the previous twelve (12) months, we have not sold your personal
          information to third parties as that term is defined by the CCPA.
        </p>
      </section>

      <section>
        <h2>Your Choices</h2>
        <p>
          <strong>Email.</strong> If you no longer want to receive certain
          communications from us via email, simply click the
          &quot;unsubscribe&quot; link in the email. Please note that you cannot
          unsubscribe from service-related correspondence from us, such as
          messages relating to your account transactions.{' '}
        </p>
        <p>
          If you want to opt-out of all electronic communication, including
          service-related correspondence, you may do so by emailing us at{' '}
          <a href={`mailto:${supportEmail}`}>{supportEmail}</a> with the
          following subject line “WITHDRAW ELECTRONIC CONSENT.” The body of the
          email must include your name, policy number, effective and expiration
          dates of the policy, the effective date of your withdrawal and whether
          you want (a) all communications to be in paper form and (b) your
          insurance policy to be sent to you in paper form.{' '}
        </p>
        <p>
          <strong>Push Notifications.</strong> You can choose whether or not to
          receive mobile push notifications from us. If you subsequently change
          your mind, you can use your mobile device&apos;s settings
          functionality to modify your preferences.
        </p>
        <p>
          <strong>Internet-Based Ads.</strong> Internet-based ads are online ads
          tailored to your likely interests based on your use of various apps
          and websites across the Internet. If you are using a browser, then
          cookies and web beacons can be used to collect information to help
          determine your likely interests. If you are using a mobile device,
          tablet, or streaming media device that includes an advertising
          identifier, then that identifier can be used to help determine your
          likely interests. For your choices about internet-based ads from us,
          please see the Cookies and Internet Advertising section (below).{' '}
        </p>
        <p>
          The CCPA provides consumers (California residents) with specific
          rights regarding their personal information. This section describes
          your CCPA rights and explains how to exercise those rights.
        </p>
        <p>
          <strong>Right to Access/Right to Know.</strong> You have the right to
          request that we disclose certain information to you about our
          collection and use of your personal information over the past 12
          months. You may request to know only the categories of personal
          information or the specific pieces of personal information we
          collected about you. Once we receive and confirm your verifiable
          consumer request we will disclose to you (as applicable to your
          request):
        </p>
        <ul className="preserve_default_browser_styles">
          <li>
            The categories of personal information we collected about you.
          </li>
          <li>
            The categories of sources for the personal information we collected
            about you.
          </li>
          <li>
            Our business or commercial purpose for collecting or selling that
            personal information.
          </li>
          <li>
            The categories of third parties with which we share that personal
            information.
          </li>
          <li>
            The specific pieces of personal information we collected about you.
          </li>
          <li>
            If we sold or disclosed your personal information for a business
            purpose, two separate lists disclosing:
            <ul className="preserve_default_browser_styles">
              <li>
                sales, identifying the personal information categories that each
                category of recipient purchased; and
              </li>
              <li>
                disclosures for a business purpose, identifying the personal
                information categories that each category of recipient obtained.
              </li>
            </ul>
          </li>
        </ul>
        <p>
          You may only make a verifiable consumer request for access twice
          within a 12-month period.
        </p>
        <p>
          <strong>Right to Request Deletion.</strong> You have the right to
          request that we delete any of your personal information that we
          collected and retained, subject to certain exceptions. Once we receive
          and confirm your verifiable consumer request, we will delete (and
          direct our service providers to delete) your personal information from
          our records, unless an exception applies.
        </p>
        <p>
          <strong>Exercising Access and Deletion Rights.</strong> To exercise
          the access or deletion rights described above, a verifiable consumer
          request can be made to us by either:
        </p>
        <ol>
          <li>
            Dial <a href="tel:1-855-327-6548">1-855-327-6548</a> Monday to
            Friday from 8 a.m. to 5 p.m. Pacific time. The representative will
            collect information from you for verification and to initiate your
            request.
          </li>
          <li>
            Submit a request through our{' '}
            <a
              href="https://www.farmers.com/california-consumer-privacy/"
              target="_blank"
              rel="noopener noreferrer"
            >
              CCPA Web Form
            </a>
            . Within three to five business days of receipt of your request, a
            representative will call you at the number you provide in order to
            collect any additional information from you necessary for
            verification and to initiate your request.
          </li>
        </ol>
        <p>
          Additional information will be required for verification purposes. The
          specific elements may vary depending on the nature of your
          relationship with us but will begin with name, address, phone number
          and date of birth. These elements will be matched with data in our
          systems. Only you, or an authorized agent, may make a request related
          to your personal information. You may also make a request on behalf of
          your minor child.
        </p>
        <ul className="preserve_default_browser_styles">
          <li>
            If you designate an Authorized Agent to act on your behalf with
            regard to a request to know or delete, you must provide us with a
            notarized affidavit. A sample affidavit is available for download
            from the{' '}
            <a
              href="https://www.farmers.com/california-consumer-privacy/"
              target="_blank"
              rel="noopener noreferrer"
            >
              CCPA Web Form
            </a>
            . Alternatively, a valid power of attorney may be provided.
            Responses to requests for access will be provided directly to the
            consumer.
          </li>
          <li>
            You may make a request to know the categories of information or
            specific pieces of information. For requests that seek the
            disclosure of specific pieces information, you may be asked to
            complete and provide a signed declaration under penalty of perjury
            that you are the consumer who is the subject of the request. A
            sample declaration is available for download from the{' '}
            <a
              href="https://www.farmers.com/california-consumer-privacy/"
              target="_blank"
              rel="noopener noreferrer"
            >
              CCPA Web Form
            </a>
            .
          </li>
        </ul>
        <p>
          If we are unable to verify you based on the information you have
          provided and the information in our systems, we will advise you that
          we cannot complete your request based on an inability to verify you.
        </p>
      </section>

      <CmsContent
        data-testid="privacyPolicy-final-content"
        entrySlug={privacyPolicySlug}
        modelSlug={cmsModelSlug}
        renderContent={createCmsContentRendererForFields([
          'Do Not Sell My Personal Information/Notice of Right to Opt Out',
          'Other Websites, Platforms and Applications',
          'Children and Minors',
          'Modifications to This Privacy Policy',
          'Cookies and Internet Advertising',
          'Your Rights to Access and Correction',
          'Signed',
        ])}
      />

      <p>
        <small>
          The above is a list of the insurers on whose behalf this privacy
          notice is being provided. It is not a comprehensive list of all
          insurers comprising the Farmers Insurance Group of Companies
          <sup>&reg;</sup>.
        </small>
      </p>
    </LayoutSelfService>
  );
}

export default PrivacyPolicyView;
